import { useRef, useState, type PointerEvent } from 'react'
import type { Locale, Product } from '../content/site'
import { payekoohTutorialSlides } from '../content/payekoohTutorial'

type PhoneScreen = 'preview' | 'home' | 'app'

const text = {
  fa: {
    intro: 'یک تجربه کوتاه از محیط اپلیکیشن',
    tapPhone: 'برای روشن کردن گوشی کلیک کنید',
    homeLabel: 'صفحه اصلی گوشی',
    openApp: 'باز کردن اپلیکیشن پایه‌کوه',
    appLabel: 'نمای آزمایشی اپلیکیشن پایه‌کوه',
    browse: 'مراحل ثبت ترک را ورق بزنید',
    demo: 'نمای آزمایشی، نه اسکرین‌شات واقعی',
    previous: 'مرحله قبل',
    next: 'مرحله بعد',
    restart: 'بازگشت به صفحه اصلی گوشی',
    close: 'خروج از نمایش اپ',
    map: 'نقشه',
    activity: 'فعالیت',
    routes: 'مسیرها',
    progress: 'مرحله',
    phone: 'پیش‌نمایش تعاملی پایه‌کوه روی گوشی آیفون',
  },
  en: {
    intro: 'A short interactive product experience',
    tapPhone: 'Tap to wake the phone',
    homeLabel: 'Phone home screen',
    openApp: 'Open the Payekooh app',
    appLabel: 'Payekooh app demonstration',
    browse: 'Explore route recording step by step',
    demo: 'Concept illustration, not an actual app screenshot',
    previous: 'Previous step',
    next: 'Next step',
    restart: 'Return to the phone home screen',
    close: 'Exit the app demonstration',
    map: 'Map',
    activity: 'Activity',
    routes: 'Routes',
    progress: 'Step',
    phone: 'Interactive Payekooh iPhone mockup',
  },
} as const

export function PayekoohInteractivePhone({ locale, product }: { locale: Locale; product: Product }) {
  const t = text[locale]
  const hostRef = useRef<HTMLDivElement>(null)
  const [screen, setScreen] = useState<PhoneScreen>('preview')
  const [step, setStep] = useState(0)
  const slide = payekoohTutorialSlides[step]
  const screenShot = product.showcaseImages?.[0]?.src ?? '/images/payekooh/tutorial/demo-01.svg'

  const openApp = () => {
    setStep(0)
    setScreen('app')
  }
  const goTo = (index: number) => setStep((index + payekoohTutorialSlides.length) % payekoohTutorialSlides.length)
  const tilt = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const box = event.currentTarget.getBoundingClientRect()
    const x = (event.clientX - box.left) / box.width - .5
    const y = (event.clientY - box.top) / box.height - .5
    hostRef.current?.style.setProperty('--pk-tilt-x', `${(-y * 9).toFixed(2)}deg`)
    hostRef.current?.style.setProperty('--pk-tilt-y', `${(x * 10).toFixed(2)}deg`)
  }
  const clearTilt = () => {
    hostRef.current?.style.setProperty('--pk-tilt-x', '0deg')
    hostRef.current?.style.setProperty('--pk-tilt-y', '0deg')
  }
  return (
    <div className="pk-phone-showcase" aria-label={t.phone}>
      <span className="pk-phone-showcase__light" aria-hidden="true" />
      <div className="pk-phone-showcase__stage" onPointerMove={tilt} onPointerLeave={clearTilt}>
        <div ref={hostRef} className="pk-phone" data-screen={screen} dir="ltr">
          <span className="pk-phone__side pk-phone__side--left" aria-hidden="true" />
          <span className="pk-phone__side pk-phone__side--right" aria-hidden="true" />
          <div className="pk-phone__frame">
            <div className="pk-phone__display">
              <span className="pk-phone__island" aria-hidden="true" />
              {screen === 'preview' && (
                <button type="button" className="pk-phone__preview" onClick={() => setScreen('home')} aria-label={t.tapPhone}>
                  <img className="pk-phone__preview-photo" src={screenShot} alt="" decoding="async" />
                  <span className="pk-phone__preview-overlay">
                    <img src="/images/payekooh/payekooh-symbol.svg" alt="" width="56" height="56" />
                    <span className="pk-phone__preview-name">{locale === 'fa' ? 'پایه‌کوه' : 'Payekooh'}</span>
                    <span className="pk-phone__preview-hint">{t.tapPhone}</span>
                    <span className="pk-phone__preview-arrow" aria-hidden="true">↗</span>
                  </span>
                </button>
              )}
              {screen === 'home' && (
                <div className="pk-phone__homescreen" lang={locale}>
                  <div className="pk-phone__clock" dir="ltr">9:41</div>
                  <div className="pk-phone__home-caption">{t.homeLabel}</div>
                  <div className="pk-phone__apps">
                    <button type="button" className="pk-phone__app-icon" onClick={openApp} aria-label={t.openApp}>
                      <span className="pk-phone__icon-tile">
                        <img src="/images/payekooh/payekooh-symbol.svg" alt="" width="58" height="58"/>
                      </span>
                      <span>{locale === 'fa' ? 'پایه‌کوه' : 'Payekooh'}</span>
                    </button>
                    <span className="pk-phone__ghost-icon" aria-hidden="true" />
                    <span className="pk-phone__ghost-icon" aria-hidden="true" />
                    <span className="pk-phone__ghost-icon" aria-hidden="true" />
                  </div>
                  <p className="pk-phone__home-hint">{locale === 'fa' ? 'روی آیکن سبز پایه‌کوه بزنید' : 'Tap the green Payekooh app icon'}</p>
                </div>
              )}
              {screen === 'app' && (
                <div className="pk-phone__app" lang={locale} dir={locale === 'fa' ? 'rtl' : 'ltr'} aria-label={t.appLabel}>
                  <div className="pk-phone__app-header">
                    <button type="button" className="pk-phone__back-home" onClick={() => setScreen('home')} aria-label={t.restart}>
                      <span aria-hidden="true">⌂</span>
                    </button>
                    <span className="pk-phone__app-brand">
                      <img src="/images/payekooh/payekooh-symbol.svg" alt="" width="22" height="22" />
                      {locale === 'fa' ? 'پایه‌کوه' : 'Payekooh'}
                    </span>
                    <span className="pk-phone__step-no" dir="ltr">{step + 1}/10</span>
                  </div>
                  <div className="pk-phone__demo-image" key={slide.id}>
                    <img src={slide.src} alt={slide.title[locale]} width="700" height="1020" decoding="async" />
                    <span className="pk-phone__demo-stamp">{t.demo}</span>
                  </div>
                  <div className="pk-phone__step-detail" aria-live="polite" aria-atomic="true">
                    <span>{t.progress} {step + 1} / 10</span>
                    <strong>{slide.title[locale]}</strong>
                  </div>
                  <div className="pk-phone__step-controls">
                    <button type="button" onClick={() => goTo(step - 1)} aria-label={t.previous}>
                      <span aria-hidden="true">{locale === 'fa' ? '›' : '‹'}</span>
                    </button>
                    <span className="pk-phone__step-line" aria-hidden="true"><i style={{width: `${(step + 1) * 10}%`}} /></span>
                    <button type="button" onClick={() => goTo(step + 1)} aria-label={t.next}>
                      <span aria-hidden="true">{locale === 'fa' ? '‹' : '›'}</span>
                    </button>
                  </div>
                  <nav className="pk-phone__app-tabs" aria-label={t.appLabel}>
                    <button type="button" className={step < 2 ? 'is-active' : ''} onClick={() => goTo(0)}><span aria-hidden="true">⌖</span>{t.map}</button>
                    <button type="button" className={step >= 2 && step < 6 ? 'is-active' : ''} onClick={() => goTo(2)}><span aria-hidden="true">◉</span>{t.activity}</button>
                    <button type="button" className={step >= 6 ? 'is-active' : ''} onClick={() => goTo(6)}><span aria-hidden="true">▤</span>{t.routes}</button>
                  </nav>
                </div>
              )}
              <span className="pk-phone__home-indicator" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>
      <div className="pk-phone-showcase__footer">
        <div className="pk-phone-showcase__brand">
          <img src="/images/payekooh/payekooh-symbol.svg" alt="" width="34" height="34" />
          <span>{screen === 'app' ? slide.title[locale] : t.intro}</span>
        </div>
        {screen === 'app' ? (
          <div className="pk-phone-showcase__step-nav">
            <button type="button" onClick={() => goTo(step - 1)}>{t.previous}</button>
            <span dir="ltr">{String(step + 1).padStart(2, '0')} / 10</span>
            <button type="button" onClick={() => goTo(step + 1)}>{t.next}</button>
          </div>
        ) : (
          <button type="button" className="pk-phone-showcase__enter" onClick={() => screen === 'preview' ? setScreen('home') : openApp()}>
            {screen === 'preview' ? t.tapPhone : t.openApp} <span aria-hidden="true">↗</span>
          </button>
        )}
      </div>
      {screen === 'app' && <p className="pk-phone-showcase__description" aria-live="polite">{slide.description[locale]}</p>}
    </div>
  )
}
